import { BRAND } from '@archetelos/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';

import { FreePlaysPill } from './FreePlaysPill';

import { Logo } from '@/components/brand/Logo';
import { JoinCta } from '@/components/layout/JoinCta';
import { resolveLandingRoute } from '@/lib/auth/auth-redirect';
import { useAuth } from '@/lib/auth/useAuth';
import { FREE_PLAY_LIMIT, freemiumQueries } from '@/lib/queries/freemium';

/**
 * Browse top bar: wordmark (serif, silver accent — the one serif slot here),
 * Editorial cross-link to the marketing surface, an inert search field (search
 * backend is Increment 3 / CONFIRM-5), the free-plays pill for signed-in free
 * members, the primary Join CTA (Spec 02 — membership conversion, self-hides
 * for members), a demoted Donate link, and the account entry. Per CONFIRM-3 the subscriber
 * dashboard lives behind this account link, not at `/`.
 */
export function TopBar() {
  const { session, profile } = useAuth();
  const signedIn = Boolean(session);
  const { data: status } = useQuery(freemiumQueries.status(signedIn));
  // Free member = signed in with no active subscription (server-derived via
  // user_has_active_subscription). The pill is hidden for anon and members.
  const showPill = signedIn && Boolean(status) && !status!.isMember;
  const dashboardHref = resolveLandingRoute({ hasSession: true, profile });

  return (
    <header className="flex items-center gap-3 border-b border-video-line bg-video-bg px-4 py-3 md:gap-6 md:px-6">
      <Link to="/" aria-label={BRAND.name}>
        <Logo className="h-6 w-auto" />
      </Link>

      <a
        href={`https://${BRAND.domain}`}
        className="hidden items-center gap-1 rounded-md border border-video-line px-3 py-1.5 font-sans text-sm text-video-text-muted transition hover:border-silver/45 hover:text-video-text md:inline-flex"
      >
        Articles
        <ArrowUpRight className="h-3.5 w-3.5" />
      </a>

      {/* Search returns with the app-search increment; spacer holds the layout. */}
      <div className="flex-1" />

      {showPill && <FreePlaysPill used={status!.used} limit={FREE_PLAY_LIMIT} />}

      <a
        href="/donate"
        className="hidden font-sans text-sm text-video-text-muted transition hover:text-video-text md:block"
      >
        Donate
      </a>

      {/* Two-tier buttons (Spec 30 H2): exactly one oxblood-filled conversion
          action at a time — Sign in for anon, Become a member (JoinCta) for a
          signed-in free member, neither for a member. Account is the silver
          outline. JoinCta self-hides for members/staff, so the filled slot is
          never empty; gating it behind signedIn keeps anon's conversion = Sign in. */}
      {signedIn ? (
        <>
          <Link
            to={dashboardHref}
            className="hidden rounded-md border border-silver/45 px-4 py-2 font-sans text-sm text-silver transition hover:border-silver hover:text-video-text md:inline-flex"
          >
            Account
          </Link>
          <JoinCta surface="video" />
        </>
      ) : (
        <Link
          to="/auth/sign-in"
          className="rounded-md bg-oxblood-browse px-4 py-2 font-sans text-sm font-medium text-video-text transition hover:bg-oxblood-live"
        >
          Sign in
        </Link>
      )}
    </header>
  );
}
